import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import {
  listMyAdvisorCodes,
  listMyLinkedClients,
  listClientScenariosForAdvisor,
  type AdvisorCodeRow,
  type ProfileRow,
  type AdvisorClientRow,
  type AdvisorScenarioRow,
} from "@/lib/admin";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { createFormatter, type Jurisdiction } from "@/lib/formatters";
import { readScenarioSummary } from "@/lib/scenarioSummary";

type LinkedClient = AdvisorClientRow & { profile: ProfileRow | null };

export default function Advisor() {
  const { user, signOut } = useAuth();
  const nav = useNavigate();
  const [codes, setCodes] = useState<AdvisorCodeRow[]>([]);
  const [clients, setClients] = useState<LinkedClient[]>([]);
  const [selected, setSelected] = useState<LinkedClient | null>(null);
  const [scenarios, setScenarios] = useState<AdvisorScenarioRow[] | null>(null);

  useEffect(() => {
    if (!user) return;
    Promise.all([listMyAdvisorCodes(user.id), listMyLinkedClients(user.id)])
      .then(([c, l]) => { setCodes(c); setClients(l); })
      .catch((e) => toast.error(e.message));
  }, [user]);

  useEffect(() => {
    if (!selected) { setScenarios(null); return; }
    listClientScenariosForAdvisor(selected.client_user_id)
      .then((s) => setScenarios(s))
      .catch((e) => toast.error(e.message));
  }, [selected]);

  const copy = async (text: string) => {
    try { await navigator.clipboard.writeText(text); toast.success("Code copied"); }
    catch { toast.error("Copy failed"); }
  };

  // Currency follows the scenario's own jurisdiction — CAD scenarios show CAD,
  // USD scenarios show USD. Never hard-code a currency here.
  const fmtMoney = (n: unknown, jurisdiction: Jurisdiction) => {
    const v = typeof n === "number" ? n : Number(n);
    if (!Number.isFinite(v)) return "—";
    return createFormatter(jurisdiction).fmtCompact(v);
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Advisor Panel — Family Loan Planner</title>
        <meta name="description" content="View your advisor code and read-only access to linked client scenarios." />
      </Helmet>
      <header className="border-b bg-primary text-primary-foreground">
        <div className="container mx-auto h-16 px-4 flex items-center justify-between">
          <Link to="/" className="font-semibold">Family Loan Planner™ · Advisor</Link>
          <div className="flex items-center gap-3 text-sm">
            <span className="opacity-80 hidden sm:inline">{user?.email}</span>
            <Button variant="secondary" size="sm" onClick={async () => { await signOut(); nav("/"); }}>Sign out</Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 space-y-6 max-w-5xl">
        <h1 className="text-2xl font-semibold">Advisor Panel</h1>

        <section>
          <h2 className="text-lg font-semibold mb-3">My advisor code{codes.length > 1 ? "s" : ""}</h2>
          {codes.length === 0 && (
            <Card className="p-4 text-sm text-muted-foreground">
              No advisor code yet. An admin must issue one for your account.
            </Card>
          )}
          <div className="grid gap-2">
            {codes.map((c) => (
              <Card key={c.code} className="p-4 flex items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-lg">{c.code}</span>
                    <Badge variant={c.active ? "default" : "secondary"}>{c.active ? "Active" : "Inactive"}</Badge>
                  </div>
                  {c.discount_note && <div className="text-xs text-muted-foreground mt-1">{c.discount_note}</div>}
                </div>
                <Button size="sm" variant="outline" onClick={() => copy(c.code)}>Copy</Button>
              </Card>
            ))}
          </div>
        </section>

        <section className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-1">
            <h2 className="text-lg font-semibold mb-3">Linked clients ({clients.length})</h2>
            <div className="space-y-2">
              {clients.length === 0 && (
                <Card className="p-4 text-sm text-muted-foreground">No clients have linked your code yet.</Card>
              )}
              {clients.map((cl) => (
                <Card
                  key={cl.client_user_id}
                  className={`p-3 cursor-pointer ${selected?.client_user_id === cl.client_user_id ? "ring-2 ring-primary" : ""}`}
                  onClick={() => setSelected(cl)}
                >
                  <div className="text-sm font-medium">{cl.profile?.display_name || cl.profile?.email || cl.client_user_id.slice(0, 8)}</div>
                  <div className="text-xs text-muted-foreground">{cl.profile?.email}</div>
                  <div className="text-xs text-muted-foreground mt-1">Linked {new Date(cl.created_at).toLocaleDateString()}</div>
                </Card>
              ))}
            </div>
          </div>

          <div className="md:col-span-2">
            <h2 className="text-lg font-semibold mb-3">
              {selected ? `${selected.profile?.display_name || selected.profile?.email || "Client"} — scenarios (read-only)` : "Select a client"}
            </h2>
            {!selected && (
              <Card className="p-6 text-sm text-muted-foreground">Pick a client on the left to view their saved scenarios.</Card>
            )}
            {selected && scenarios === null && <p className="text-sm text-muted-foreground">Loading…</p>}
            {selected && scenarios && scenarios.length === 0 && (
              <Card className="p-6 text-sm text-muted-foreground">This client has no saved scenarios yet.</Card>
            )}
            {selected && scenarios && scenarios.length > 0 && (
              <Card className="overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-xs uppercase tracking-wide">
                    <tr>
                      <th className="text-left p-3">Loan ID</th>
                      <th className="text-left p-3">Name</th>
                      <th className="text-right p-3">Loan amount</th>
                      <th className="text-left p-3">Purpose</th>
                      <th className="text-left p-3">Stewardship</th>
                      <th className="text-left p-3">Created</th>
                      <th className="text-left p-3">Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scenarios.map((s) => {
                      const summary = readScenarioSummary(s.data);
                      const juris: Jurisdiction = summary.jurisdiction;
                      const { amount, purpose, stewardship: steward } = summary;
                      return (
                        <tr key={s.id} className="border-t">
                          <td className="p-3 font-mono text-xs">{s.loan_id || "—"}</td>
                          <td className="p-3">{s.name || <span className="text-muted-foreground">Untitled draft</span>}</td>
                          <td className="p-3 text-right font-mono">{amount === null ? "—" : fmtMoney(amount, juris)}</td>
                          <td className="p-3">{purpose}</td>
                          <td className="p-3 text-muted-foreground">{steward || "—"}</td>
                          <td className="p-3 text-muted-foreground">{new Date(s.created_at).toLocaleDateString()}</td>
                          <td className="p-3 text-muted-foreground">{new Date(s.updated_at).toLocaleDateString()}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Card>
            )}
            {selected && (
              <p className="text-xs text-muted-foreground mt-3">Read-only view. Editing client scenarios is not available in this phase.</p>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}